import { DateTime } from 'luxon';
import type { APIKeyDTO } from '@/api/trafikportalen';

export type ApiKeyStatus = 'active' | 'expired' | 'revoked';

type ApiKeyTiming = Pick<APIKeyDTO, 'revoked_at' | 'expires_at'>;

const STATUS_RANK: Record<ApiKeyStatus, number> = {
  active: 0,
  expired: 1,
  revoked: 2,
};

export const getApiKeyStatus = (
  apiKey: ApiKeyTiming,
  now: DateTime = DateTime.now(),
): ApiKeyStatus => {
  if (apiKey.revoked_at) {
    return 'revoked';
  }

  if (!apiKey.expires_at) {
    return 'active';
  }

  const expiresAt = DateTime.fromISO(apiKey.expires_at);
  if (expiresAt.isValid && expiresAt <= now) {
    return 'expired';
  }

  return 'active';
};

export const sortApiKeys = <T extends ApiKeyTiming & { created_at: string }>(
  apiKeys: T[],
  now: DateTime = DateTime.now(),
): T[] =>
  [...apiKeys].sort((left, right) => {
    const statusDiff =
      STATUS_RANK[getApiKeyStatus(left, now)] -
      STATUS_RANK[getApiKeyStatus(right, now)];
    if (statusDiff !== 0) {
      return statusDiff;
    }

    return right.created_at.localeCompare(left.created_at);
  });

// The date input is a calendar day. The key stops working at the end of that
// day in the user's local time.
export const dateInputToExpiresAt = (date: string): string | null => {
  const parsed = DateTime.fromFormat(date, 'yyyy-MM-dd');
  if (!parsed.isValid) {
    return null;
  }

  return parsed.endOf('day').toUTC().toISO({ suppressMilliseconds: true });
};
